<script setup lang="ts">
/**
 * FE-024 — mobile bottom nav: behaviour around the DS `FairBottomNav` (ui/patterns).
 * Routing, the sign-in gate for auth tabs and hide-on-scroll (agreed with design,
 * not in the file) live here; markup, styles and the FE-017 icon motion live in the kit.
 */
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { FairBottomNav, type FairBottomNavItem } from '@/ui/patterns/FairBottomNav'
import { useAuthModal, useTokenStore } from '@/features/auth/store'
import {
  FAIR_BOTTOM_NAV_END_TABS,
  FAIR_BOTTOM_NAV_ROOT_CLASS,
  FAIR_BOTTOM_NAV_START_TABS,
  resolveFairBottomNavActive,
  resolveFairBottomNavScrollIntent,
  type FairBottomNavTab,
} from '../fair-bottom-nav.data'
import { useFairBottomNav } from '../store/useFairBottomNavStore'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const tokenStore = useTokenStore()
const authModal = useAuthModal()
const nav = useFairBottomNav()

const activeTab = computed(() => resolveFairBottomNavActive(route.path))
const hidden = computed(() => nav.hidden)

const TABS_BY_ID = new Map<string, FairBottomNavTab>(
  [...FAIR_BOTTOM_NAV_START_TABS, ...FAIR_BOTTOM_NAV_END_TABS].map((tab) => [tab.id, tab]),
)

function toItem(tab: FairBottomNavTab): FairBottomNavItem {
  return {
    id: tab.id,
    label: t(`fair_bottom_nav.${tab.labelKey}`),
    icon: tab.icon,
    activeIcon: tab.activeIcon,
    parts: tab.parts,
    motion: tab.motion,
  }
}

const startItems = computed(() => FAIR_BOTTOM_NAV_START_TABS.map(toItem))
const endItems = computed(() => FAIR_BOTTOM_NAV_END_TABS.map(toItem))

function select(item: FairBottomNavItem) {
  const tab = TABS_BY_ID.get(item.id)
  if (!tab) return
  if (tab.auth && !tokenStore.accessToken) {
    authModal.showAuthModal('signInSignUpWrapper')
    return
  }
  if (activeTab.value === tab.id && route.path === tab.to) return
  void router.push(tab.to)
}

/* ——— Hide on scroll down, show on scroll up ——— */
let lastY = 0
function onScroll() {
  if (nav.claimed) return
  const y = window.scrollY
  const intent = resolveFairBottomNavScrollIntent({ scrollY: y, lastY, hidden: nav.hidden })
  if (intent) nav.setHidden(intent === 'hide')
  lastY = Math.max(0, y)
}

onMounted(() => {
  document.documentElement.classList.add(FAIR_BOTTOM_NAV_ROOT_CLASS)
  nav.setMounted(true)
  lastY = window.scrollY
  window.addEventListener('scroll', onScroll, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  document.documentElement.classList.remove(FAIR_BOTTOM_NAV_ROOT_CLASS)
  nav.setMounted(false)
})

// A new page starts with the bar on screen.
watch(() => route.path, () => {
  nav.setHidden(false)
  lastY = 0
})
</script>

<template>
  <FairBottomNav
    :start-items="startItems"
    :end-items="endItems"
    :active-id="activeTab"
    :hidden="hidden"
    :aria-label="t('fair_bottom_nav.aria_label')"
    @select="select"
  />
</template>
